// Copyright 2026, Imprivata, Inc.  All rights reserved.

import type { EndpointGroup } from '@api/types';
import SearchInput from '@components/searchInput/SearchInput';
import { ErrorCode } from '@errorHandler/constants';
import type { ApiError } from '@errorHandler/errors';
import { Modal } from '@imprivata-cloud/components';
import { Alert, Button, Input } from '@imprivata-cloud/design-system';
import { Radio, Spin } from 'antd';
import { Trash2 } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QuestionImg from '../../../../assets/icons/circle-question-mark.svg?react';
import {
  type AssignOutcome,
  useEndpointGroupMutations,
  useEndpointGroups,
  useSessionErrorEscalation,
} from '../hooks/useEndpointGroups';
import { checkGroupName, GROUP_NAME_MAX_LENGTH, matchesGroupSearch } from '../utils/endpointGroups';

const TRANSLATION_ROOT = 'portal.settings.endpoints.groups';
const DIALOG_WIDTH = 560;

type NameError = 'empty' | 'duplicate' | 'limit' | 'failed';

const NAME_ERROR_KEYS: Record<NameError, string> = {
  empty: `${TRANSLATION_ROOT}.errors.name-empty`,
  duplicate: `${TRANSLATION_ROOT}.errors.name-duplicate`,
  limit: `${TRANSLATION_ROOT}.manage.limit-reached`,
  failed: `${TRANSLATION_ROOT}.errors.name-failed`,
};

const byName = (a: EndpointGroup, b: EndpointGroup) => a.displayName.localeCompare(b.displayName);

const toNameError = (error: ApiError): NameError => {
  switch (error.code) {
    case ErrorCode.DISPLAY_NAME_TAKEN:
      return 'duplicate';
    // The BFF owns the group cap, so it can change without a UI change; the UI only reports it
    // (user, 2026-10-16).
    case ErrorCode.MANUAL_GROUP_LIMIT_EXCEEDED:
      return 'limit';
    default:
      return 'failed';
  }
};

const GroupsLoading = () => {
  const { t } = useTranslation();
  return <Spin className="endpoint-groups-loading" aria-label={t(`${TRANSLATION_ROOT}.loading`)} />;
};

type GroupSearchProps = { value: string; onChange: (value: string) => void };

const GroupSearch = ({ value, onChange }: GroupSearchProps) => {
  const { t } = useTranslation();
  return (
    <div className="endpoint-groups-search">
      <SearchInput
        placeholder={t(`${TRANSLATION_ROOT}.search-placeholder`)}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );
};

type GroupsLoadStateProps = { isLoading: boolean; isError: boolean; onRetry: () => void };

const GroupsLoadState = ({ isLoading, isError, onRetry }: GroupsLoadStateProps) => {
  const { t } = useTranslation();
  if (isLoading) {
    return <GroupsLoading />;
  }
  if (isError) {
    return (
      <Alert
        className="endpoint-groups-alert"
        status="error"
        title={t(`${TRANSLATION_ROOT}.errors.load-failed`)}
        actions={
          <Button variant="link" size="sm" onClick={onRetry}>
            {t(`${TRANSLATION_ROOT}.retry`)}
          </Button>
        }
      />
    );
  }
  return null;
};

type DraftRowProps = { existing: readonly EndpointGroup[]; onAdded: (displayName: string) => void };

/** One "New group name" field with its own Add; Figma lets several be filled in at once. */
const DraftRow = ({ existing, onAdded }: DraftRowProps) => {
  const { t } = useTranslation();
  const escalate = useSessionErrorEscalation();
  const { create } = useEndpointGroupMutations();
  const nameInput = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<NameError | null>(null);

  // A row only appears on open or on "Add new group", so it takes focus (Figma). On open the
  // Modal's portal is attached after this effect runs, so the focus waits a frame.
  useEffect(() => {
    const frame = requestAnimationFrame(() => nameInput.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, []);

  const rejectName = (error: NameError) => {
    setNameError(error);
    nameInput.current?.focus();
  };

  const handleAdd = (event: FormEvent) => {
    event.preventDefault();
    const check = checkGroupName(name, existing);
    if (check.kind !== 'valid') {
      rejectName(check.kind);
      return;
    }
    create.mutate(check.displayName, {
      onSuccess: () => onAdded(check.displayName),
      onError: error => {
        escalate(error);
        rejectName(toNameError(error));
      },
    });
  };

  return (
    <form className="endpoint-groups-add" onSubmit={handleAdd} noValidate>
      <Input
        ref={nameInput}
        inputSize="md"
        aria-label={t(`${TRANSLATION_ROOT}.manage.name-label`)}
        placeholder={t(`${TRANSLATION_ROOT}.manage.name-placeholder`)}
        value={name}
        maxLength={GROUP_NAME_MAX_LENGTH}
        error={nameError !== null}
        errorMessage={nameError ? t(NAME_ERROR_KEYS[nameError]) : undefined}
        onChange={e => {
          setName(e.target.value);
          setNameError(null);
        }}
        fullWidth
      />
      <Button
        type="submit"
        variant="primary"
        disabled={create.isPending}
        loading={create.isPending}
      >
        <span aria-hidden="true">+</span> {t(`${TRANSLATION_ROOT}.manage.add`)}
      </Button>
    </form>
  );
};

type ManageGroupsDialogProps = {
  open: boolean;
  onClose: () => void;
  announce: (message: string) => void;
};

// The zero state's field is shown before any "Add new group" click, under this fixed key.
const FIRST_DRAFT = 0;

/** Lives inside the Modal so `destroyOnHidden` drops the drafts when the dialog closes. */
const ManageGroupsBody = ({ announce }: Pick<ManageGroupsDialogProps, 'announce'>) => {
  const { t } = useTranslation();
  const escalate = useSessionErrorEscalation();
  const { groups, remove } = useEndpointGroups();
  const nextDraft = useRef(FIRST_DRAFT + 1);

  const [drafts, setDrafts] = useState<readonly number[]>([]);
  const [wasZeroState, setWasZeroState] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<EndpointGroup | null>(null);
  const [deleteFailed, setDeleteFailed] = useState(false);
  const cancelDeleteRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);

  const allGroups = groups.data ?? [];
  const sorted = useMemo(() => [...(groups.data ?? [])].sort(byName), [groups.data]);
  // Once the first load settles, a refetch must not hide the drafts (a failed query goes back to
  // pending while it refetches). A failed load shows the zero state too (user decision).
  const loaded = groups.isFetched;
  const zeroState = loaded && allGroups.length === 0;

  // Entering the zero state adds its field to the drafts, so it stays until its own group is
  // created: the refetch that brings the first group (its own, or another admin's) lands before
  // the create's callback, and a derived field would unmount first and lose it.
  if (zeroState !== wasZeroState) {
    setWasZeroState(zeroState);
    if (zeroState) {
      setDrafts(current => (current.length === 0 ? [FIRST_DRAFT] : current));
    }
  }

  const addDraft = () => {
    // Taken outside the updater, which must stay pure (StrictMode calls it twice).
    const draft = nextDraft.current;
    nextDraft.current += 1;
    setDrafts(current => [...current, draft]);
  };

  const handleAdded = (draft: number, displayName: string) => {
    setDrafts(current => current.filter(id => id !== draft));
    announce(t(`${TRANSLATION_ROOT}.announce.added`, { name: displayName }));
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete) {
      return;
    }
    const { groupId, displayName } = pendingDelete;
    const deleted = () => {
      setPendingDelete(null);
      setDeleteFailed(false);
      announce(t(`${TRANSLATION_ROOT}.announce.deleted`, { name: displayName }));
    };
    remove.mutate(groupId, {
      onSuccess: deleted,
      onError: error => {
        // Delete is not idempotent: a group another admin already deleted answers 404, and the
        // admin's intent (the group is gone) holds, so it is not reported as a failure.
        if (error.code === ErrorCode.GROUP_NOT_FOUND) {
          deleted();
          return;
        }
        escalate(error);
        setDeleteFailed(true);
      },
    });
  };

  // Ignores the close icon and Esc while the delete runs, as Cancel is disabled then.
  const closeDeleteConfirm = () => {
    if (remove.isPending) {
      return;
    }
    setPendingDelete(null);
    setDeleteFailed(false);
  };

  return (
    <>
      {groups.isLoading && <GroupsLoading />}
      {loaded && sorted.length > 0 && (
        <ul
          className="endpoint-groups-list"
          aria-label={t(`${TRANSLATION_ROOT}.manage.list-label`)}
        >
          {sorted.map(group => (
            <li key={group.groupId} className="endpoint-groups-row">
              <span className="endpoint-groups-row-name" title={group.displayName}>
                {group.displayName}
              </span>
              <Button
                variant="glyph"
                icon={<Trash2 size={16} />}
                aria-label={t(`${TRANSLATION_ROOT}.manage.delete-label`, {
                  name: group.displayName,
                })}
                onClick={() => setPendingDelete(group)}
              >
                {''}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {drafts.length > 0 && (
        <div className="endpoint-groups-drafts">
          {drafts.map(draft => (
            <DraftRow
              key={draft}
              existing={allGroups}
              onAdded={displayName => handleAdded(draft, displayName)}
            />
          ))}
        </div>
      )}

      {loaded && (
        <Button className="endpoint-groups-add-another" variant="link" size="sm" onClick={addDraft}>
          {/* One flex item, so the link underline runs unbroken under the + and the label. */}
          <span>
            <span aria-hidden="true">+</span> {t(`${TRANSLATION_ROOT}.manage.add-another`)}
          </span>
        </Button>
      )}

      <Modal
        open={pendingDelete !== null}
        centered
        // "Remove <group name>?" names the dialog; the icon is decorative.
        title={
          <>
            <QuestionImg className="endpoint-groups-confirm-icon" aria-hidden="true" />
            <span className="endpoint-groups-confirm-name">
              {t(`${TRANSLATION_ROOT}.delete.title`, {
                displayName: pendingDelete?.displayName ?? '',
              })}
            </span>
          </>
        }
        width={DIALOG_WIDTH}
        onCancel={closeDeleteConfirm}
        // The safe choice takes focus (Figma confirmation pattern, as on Users).
        afterOpenChange={open => open && cancelDeleteRef.current?.focus()}
        footer={
          <div className="endpoint-groups-confirm-actions">
            <Button
              variant="secondary"
              onClick={handleConfirmDelete}
              disabled={remove.isPending}
              loading={remove.isPending}
            >
              {t(`${TRANSLATION_ROOT}.delete.confirm`)}
            </Button>
            <Button
              ref={cancelDeleteRef}
              variant="primary"
              onClick={closeDeleteConfirm}
              disabled={remove.isPending}
            >
              {t(`${TRANSLATION_ROOT}.delete.cancel`)}
            </Button>
          </div>
        }
        maskClosable={false}
        destroyOnHidden
        className="endpoint-groups-dialog endpoint-groups-confirm"
      >
        {/* An empty group has no endpoints to lose overrides, so only the question shows. */}
        {!!pendingDelete?.memberCount && (
          <p className="endpoint-groups-confirm-message">
            {t(`${TRANSLATION_ROOT}.delete.message`, { count: pendingDelete.memberCount })}
          </p>
        )}
        {deleteFailed && (
          <Alert
            className="endpoint-groups-alert"
            status="error"
            title={t(`${TRANSLATION_ROOT}.errors.delete-failed`)}
          />
        )}
      </Modal>
    </>
  );
};

export const ManageGroupsDialog = ({ open, onClose, announce }: ManageGroupsDialogProps) => {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      title={t(`${TRANSLATION_ROOT}.manage.title`)}
      width={DIALOG_WIDTH}
      onCancel={onClose}
      footer={null}
      maskClosable={false}
      destroyOnHidden
      className="endpoint-groups-dialog endpoint-groups-manage"
    >
      <ManageGroupsBody announce={announce} />
    </Modal>
  );
};

type AssignGroupDialogProps = {
  open: boolean;
  endpointUuids: readonly string[];
  onClose: () => void;
  onAssigned: (count: number, groupName: string) => void;
};

export const AssignGroupDialog = ({
  open,
  endpointUuids,
  onClose,
  onAssigned,
}: AssignGroupDialogProps) => {
  const { t } = useTranslation();
  const escalate = useSessionErrorEscalation();
  const { groups, assign } = useEndpointGroups();

  const [search, setSearch] = useState('');
  const [groupId, setGroupId] = useState<string | null>(null);
  const [failure, setFailure] = useState<Extract<AssignOutcome, { kind: 'failed' }> | null>(null);

  const allGroups = groups.data ?? [];
  const chosen = allGroups.find(group => group.groupId === groupId) ?? null;
  const visible = useMemo(
    () => (groups.data ?? []).filter(group => matchesGroupSearch(group, search)).sort(byName),
    [groups.data, search],
  );

  const handleClose = () => {
    setSearch('');
    setGroupId(null);
    setFailure(null);
    onClose();
  };

  // The close icon and Esc also cancel; a dismissal mid-run would leave its chunks running with
  // the page's Remove enabled, so both are ignored until the assignment settles.
  const handleCancel = () => {
    if (!assign.isPending) {
      handleClose();
    }
  };

  const handleAssign = () => {
    if (!chosen) {
      return;
    }
    setFailure(null);
    assign.mutate(
      { groupId: chosen.groupId, endpointUuids },
      {
        onSuccess: outcome => {
          if (outcome.kind === 'done') {
            handleClose();
            onAssigned(outcome.changed, chosen.displayName);
            return;
          }
          escalate(outcome.error);
          setFailure(outcome);
        },
      },
    );
  };

  const failureTitle =
    failure?.error.code === ErrorCode.GROUP_MEMBER_LIMIT_EXCEEDED
      ? t(`${TRANSLATION_ROOT}.errors.member-limit`, { name: chosen?.displayName })
      : t(`${TRANSLATION_ROOT}.errors.assign-failed`);
  const showList = !groups.isLoading && !groups.isError;

  return (
    <Modal
      open={open}
      title={t(`${TRANSLATION_ROOT}.assign.title`)}
      width={DIALOG_WIDTH}
      onOk={handleAssign}
      okText={t(`${TRANSLATION_ROOT}.assign.confirm`)}
      okButtonProps={{ disabled: !chosen || assign.isPending, loading: assign.isPending }}
      onCancel={handleCancel}
      cancelText={t('common.cancel')}
      cancelButtonProps={{ disabled: assign.isPending }}
      maskClosable={false}
      destroyOnHidden
      className="endpoint-groups-dialog"
    >
      {allGroups.length > 0 && <GroupSearch value={search} onChange={setSearch} />}

      <GroupsLoadState
        isLoading={groups.isLoading}
        isError={groups.isError}
        onRetry={() => void groups.refetch()}
      />
      {showList && allGroups.length === 0 && (
        <p className="endpoint-groups-empty">{t(`${TRANSLATION_ROOT}.assign.empty`)}</p>
      )}
      {showList && allGroups.length > 0 && visible.length === 0 && (
        <p className="endpoint-groups-empty">{t(`${TRANSLATION_ROOT}.no-results`)}</p>
      )}
      {showList && visible.length > 0 && (
        // antd's Radio.Group drops `role`, so the wrapper carries the group semantics.
        <div role="radiogroup" aria-label={t(`${TRANSLATION_ROOT}.assign.list-label`)}>
          <Radio.Group
            className="endpoint-groups-list endpoint-groups-radios"
            value={groupId}
            disabled={assign.isPending}
            onChange={e => {
              setGroupId(e.target.value);
              // The failure names the group it was for, so it goes once another is chosen.
              setFailure(null);
            }}
          >
            {visible.map(group => (
              <Radio key={group.groupId} value={group.groupId}>
                {group.displayName}
              </Radio>
            ))}
          </Radio.Group>
        </div>
      )}

      {failure && (
        <Alert
          className="endpoint-groups-alert"
          status="error"
          title={failureTitle}
          message={
            failure.changed > 0
              ? t(`${TRANSLATION_ROOT}.errors.partial`, {
                  changed: failure.changed,
                  total: failure.total,
                })
              : undefined
          }
        />
      )}
    </Modal>
  );
};
